import React, { useMemo } from "react";
import {
  AutoRenewButton,
  GracePeriodButton,
  ReactivateButton,
  RenewButton,
  SubscribeButton,
  UpgradeButton,
} from "scmp-sdk/components";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { ScmpPlusButton } from "scmp-sdk/components/rosetta/subscription-button/scmp-plus-button";
import type { SubscriptionButtonContainerProps } from "scmp-sdk/components/rosetta/subscription-button/styles";
import { SubscriptionButtonContainer } from "scmp-sdk/components/rosetta/subscription-button/styles";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { EpaperButton } from "./epaper-button";

export type Props = {
  className?: string;
  location?: "footer" | "masthead" | "print-button";
  variant?: SubscriptionButtonContainerProps["$variant"];
};
export const SubscriptionButton: React.FunctionComponent<Props> = ({
  className,
  location = "masthead",
  variant,
}) => {
  const { rosettaInstance } = useSdkContext();
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const mastheadButton = rosettaInstance?.rulesEngine.getOutcome("ui.masthead.button");

  const buttonType = useMemo(
    () =>
      mastheadButton?.getDisplayVariant({
        shouldIncludeEpaper: true,
        shouldIncludeScmpPlus: !isMobile,
      }),
    [isMobile, mastheadButton],
  );

  const computedVariant = useMemo(
    () => (buttonType === "SCMPPLUS" ? "dark-blue" : variant),
    [buttonType, variant],
  );

  const handlerRenderButton = () => {
    switch (buttonType) {
      case "AUTORENEW":
        return <AutoRenewButton className={className} />;
      case "EPAPER":
        return <EpaperButton className={className} />;
      case "GRACEPERIOD":
        return <GracePeriodButton className={className} />;
      case "REACTIVATE":
        return <ReactivateButton className={className} location={location} />;
      case "RENEW":
        return <RenewButton className={className} />;
      case "SCMPPLUS": {
        const isMasthead = location === "masthead";

        return (
          <ScmpPlusButton
            className={className}
            withImpressionTracking={isMasthead}
            withUpdateIndicator={isMasthead}
          />
        );
      }
      case "SUBSCRIBE":
        return <SubscribeButton className={className} location={location} />;
      case "UPGRADE":
        return <UpgradeButton className={className} />;
      default:
        return null;
    }
  };

  return (
    <SubscriptionButtonContainer $variant={computedVariant}>
      {handlerRenderButton()}
    </SubscriptionButtonContainer>
  );
};

SubscriptionButton.displayName = "SubscriptionButton";
